import React, { useEffect, useRef, useState } from 'react';
import { MessageSquareWarning, Undo2, UserX, Moon, RefreshCw, Repeat } from 'lucide-react';
import { problemsData } from '../../data/mock';
import motherImage from '@/assets/images/mother-lost-temper.jpg';
import challengeVideo from '@/assets/images/challenge.mp4';
const ProblemSection = () => {
  const sectionRef = useRef(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsVisible(true);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const iconMap = {
    MessageSquareWarning,
    Undo2,
    UserX,
    Moon,
    RefreshCw,
    Repeat
  };

  return (
    <section
      ref={sectionRef}
      className="py-32 bg-white relative overflow-hidden"
    >
      <div className="max-w-6xl mx-auto px-8">
        <div className="grid lg:grid-cols-2 gap-20 items-center">
          {/* Left - Content */}
          <div>
            {/* Section Label */}
            <div className={`mb-6 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              <span className="text-sm text-[#202020]/40 uppercase tracking-widest">The Challenge</span>
            </div>

            {/* Title */}
            <h2 className={`text-3xl md:text-4xl lg:text-5xl font-normal text-[#202020] mb-12 leading-tight tracking-tight transition-all duration-1000 delay-100 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
              {problemsData.title}
            </h2>

            {/* Problems List */}
            <div className="space-y-5 mb-12">
              {problemsData.problems.map((problem, index) => {
                const Icon = iconMap[problem.icon];
                return (
                  <div
                    key={index}
                    className={`flex items-start gap-4 group transition-all duration-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}
                    style={{ transitionDelay: `${(index + 2) * 80}ms` }}
                  >
                    <div className="flex-shrink-0 w-10 h-10 rounded-lg bg-[#efede6] flex items-center justify-center group-hover:bg-[#202020] transition-colors duration-300">
                      <Icon className="w-4 h-4 text-[#202020]/60 group-hover:text-[#efede6] transition-colors duration-300" />
                    </div>
                    <p className="text-[#202020]/70 leading-relaxed pt-2">
                      {problem.text}
                    </p>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Right - Emotional Image */}
          <div className={`transition-all duration-1000 delay-500 ${isVisible ? 'opacity-100 translate-x-0' : 'opacity-0 translate-x-8'}`}>
            <div className="relative">
              <div className="rounded-3xl overflow-hidden shadow-2xl">
                <video
                  src={challengeVideo}
                  autoPlay
                  loop
                  muted
                  playsInline
                  className="w-full h-84 md:h-96 object-cover grayscale brightness-90"
                />
                {/* <img 
                  src={motherImage} 
                  alt="Stressed parent"
                  className="w-full h-[500px] object-cover grayscale"
                /> */}
                <div className="absolute rounded-3xl inset-0 bg-gradient-to-t from-[#202020]/40 to-transparent" />
              </div>
              <div className="absolute -bottom-6 -right-6 bg-[#202020] rounded-2xl p-6 shadow-xl max-w-xs">
                <p className="text-[#efede6] text-lg font-medium italic mb-0">"Why do I keep losing my temper?"</p>
              </div>
            </div>
          </div>
        </div>

        {/* Insight Cards */}
        <div className="grid md:grid-cols-2 gap-8 mt-20">
          <div className={`bg-[#202020] rounded-3xl p-10 transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h3 className="text-2xl text-[#efede6] font-normal mb-4 leading-snug">{problemsData.insight.title}</h3>
            <p className="text-[#efede6]/60 leading-relaxed">{problemsData.insight.content}</p>
          </div>

          <div className={`bg-[#efede6] rounded-3xl p-10 transition-all duration-1000 delay-800 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'}`}>
            <h3 className="text-2xl text-[#202020] font-normal mb-4 leading-snug">{problemsData.solution.title}</h3>
            <p className="text-[#202020]/60 leading-relaxed">{problemsData.solution.content}</p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ProblemSection;
